import { useTheme } from "@mui/material";

/**
 * MuiColor is a type for the names of the first-class MUI color palette
 * entities.
 */
export type MuiColor =
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

/**
 * MuiTypographyColor adds values of MuiColor which are only recognized by the
 * <Typography> component.
 */
export type MuiTypographyColor =
  | MuiColor
  | "textPrimary"
  | "textSecondary"
  | "textDisabled";

/**
 * useMuiColor returns the color value of a given MUI color token.
 */
export function useMuiColor(muiColor: MuiTypographyColor | undefined): string {
  const theme = useTheme();

  switch (muiColor) {
    case "textPrimary": {
      return theme.palette.text.primary;
    }
    case "textSecondary": {
      return theme.palette.text.secondary;
    }
    case "textDisabled": {
      return theme.palette.text.disabled;
    }
    case undefined: {
      return "";
    }
    default: {
      return theme.palette[muiColor].main;
    }
  }
}

/**
 * useMuiColorStyle returns the dynamic style required to apply a given MUI
 * color token to a markup element.
 */
export function useMuiColorStyle(
  muiColor: MuiTypographyColor | undefined
): Record<string, string> {
  const colorValue = useMuiColor(muiColor);
  return { color: colorValue };
}

/**
 * colorFallback allows you to conditionally apply MUI color tokens using a
 * series of fallbacks shaped like an object parameter to `classes`; for an
 * object {[k1]: v1, [k2]: v2, ...} in insertion order, it returns the first k_n
 * such that v_n is truthy.
 *
 * this was invented for backwards compatibility with the way a lot of
 * `SummaryCardItem`s were styled, but it's potentially useful for other things
 * too.
 */
export function colorFallback<T extends MuiTypographyColor>(
  colors: Record<T, any>
): T | undefined {
  for (const color in colors) {
    if (colors[color]) {
      return color;
    }
  }

  return undefined;
}
